import {
  IFlight,
  ILegDescription,
  ISearchInfo,
} from "@/feature/flight/search/types/flight";

export interface IExtractFlightFilter {
  flightResult: IFlight[] | undefined;
  searchInfo: ISearchInfo | null | undefined;
}

export const extractFlightFilterData = ({
  flightResult,
  searchInfo,
}: IExtractFlightFilter): IExtractFlightReturnType => {
  if (!flightResult) return null;
  // const startTime = performance.now();

  const aircraft_set = new Set<string>();
  let layover: string[] = [];
  const layover_city = new Map();
  const airlines_lowest_price = new Map<string, AirlineLowestPrice>();
  const flightNumberSet = new Set<string>();

  let minPrice = Infinity;
  let maxPrice = -Infinity;

  let minLayover = Infinity;
  let maxLayover = -Infinity;

  let stoppage = 0;

  const TIME_RANGES = "00-06 ; 06-12 ; 12-18 ; 18-00";
  const time_frame: TimeFrame = { departure: [], arrival: [] };

  searchInfo?.leg_description?.forEach(
    ({ departureLocation, arrivalLocation }) => {
      time_frame.departure.push({
        code: departureLocation,
        times: TIME_RANGES,
      });
      time_frame.arrival.push({ code: arrivalLocation, times: TIME_RANGES });
    },
  );

  let max_stoppage_per_leg: number[] = [];

  if (searchInfo?.leg_description) {
    // Initialize max stoppage per leg
    max_stoppage_per_leg = Array(searchInfo.leg_description.length).fill(0);
  }

  flightResult?.forEach((flightData) => {
    const { fare, flights } = flightData;
    const payable = Number(fare?.payable ?? 0);

    const airlineMeta = {
      code: flightData.carrier_code,
      name: flightData.carrier_name,
      logo: flightData.carrier_logo,
    };
    const airlineKey = `code:${airlineMeta.code.toLowerCase()}`;

    // Collect minium and maximum price
    minPrice = Math.min(minPrice, payable);
    maxPrice = Math.max(maxPrice, payable);

    flights?.forEach((flightLeg, legIndex) => {
      if (legIndex < max_stoppage_per_leg.length) {
        max_stoppage_per_leg[legIndex] = Math.max(
          max_stoppage_per_leg[legIndex],
          flightLeg.stoppage || 0,
        );
      }
    });

    // Collect flight numbers (both operating and marketing)
    flights?.forEach((flightLeg) => {
      flightLeg.options?.forEach((option) => {
        // if (option.carrier?.carrier_operating_flight_number) {
        //   flightNumberSet.add(String(option.carrier.carrier_operating_flight_number));
        // }
        if (option.carrier?.carrier_marketing_flight_number) {
          flightNumberSet.add(
            String(option.carrier.carrier_marketing_flight_number),
          );
        }
      });
    });

    // Collect lowest price object
    const existingLowestPrice = airlines_lowest_price.get(airlineKey);
    const nextAirlineEntry: AirlineLowestPrice = {
      airline_code: airlineMeta.code,
      airline_logo: flightData.carrier_logo,
      airline_name: airlineMeta.name,
      price: payable,
    };
    if (!existingLowestPrice || payable < existingLowestPrice.price) {
      airlines_lowest_price.set(airlineKey, nextAirlineEntry);
    }

    // Collect layover cities 
    flights?.forEach((flight) => {
      flight?.layover_time?.forEach((layover) => {
        if (layover > maxLayover) maxLayover = layover;
        if (layover < minLayover) minLayover = layover;
      });

      stoppage = Math.max(stoppage, flight.stoppage ?? 0);

      if (flight?.layover_time && flight?.layover_time.length > 1) {
        flight?.options?.forEach(({ departure, arrival }) => {
          if ((flight?.stoppage ?? 0) > 0) {
            if (!layover_city.has(arrival?.airport_code)) {
              layover_city.set(arrival?.airport_code, {
                code: arrival?.airport_code,
                city: arrival?.city?.split(" - ")[0],
                airport: arrival?.airport,
              });
            }
            if (!layover_city.has(departure?.airport_code)) {
              layover_city.set(departure?.airport_code, {
                code: departure?.airport_code,
                city: departure?.city?.split(" - ")[0],
                airport: departure?.airport,
              });
            }
            searchInfo?.leg_description?.some((leg) => {
              layover_city.delete(leg.arrivalLocation);
              layover_city.delete(leg.departureLocation);
            });
          }
        });
      }

      // Collect layover time range static
      if (flight?.layover_time && flight?.layover_time?.length > 1) {
        layover = ["0h - 5h", "5h - 10h", "10h - 15h", "15h+"];
      }

      // Collect aircraft names
      flight?.options?.forEach(({ carrier }) => {
        if (carrier?.carrier_aircraft_name)
          if (!aircraft_set.has(carrier.carrier_aircraft_name))
            aircraft_set.add(carrier.carrier_aircraft_name);
      });

      // Collect maximum and minimum layover time
    });
  });

  const sortedAirlinesLowestPrice = Array.from(airlines_lowest_price.values())
    .sort((a, b) => {
      if (a.price !== b.price) return a.price - b.price;
      return a.airline_name.localeCompare(b.airline_name);
    });

  // const endTime = performance.now();
  // const duration = endTime - startTime;

  return {
    aircraft: Array.from(aircraft_set),
    layover_city: Array.from(layover_city?.values()),
    layover,
    price_range:
      Number.isFinite(minPrice) && Number.isFinite(maxPrice)
        ? [minPrice, maxPrice]
        : undefined,
    airlines_lowest_price: sortedAirlinesLowestPrice,
    time_frame,
    layover_range:
      Number.isFinite(minLayover) && Number.isFinite(maxLayover)
        ? [minLayover, maxLayover]
        : undefined,
    stoppage,
    max_stoppage_per_leg,
    leg_descriptions: searchInfo?.leg_description || [],
    flight_numbers: Array.from(flightNumberSet),
  };
};

export type IExtractFlightReturnType =
  | {
    aircraft: string[];
    layover_city: { code: string; city: string; airport: string }[];
    layover: string[];
    price_range?: number[];
    airlines_lowest_price: AirlineLowestPrice[];
    time_frame?: TimeFrame;
    layover_range?: number[];
    stoppage?: number;
    max_stoppage_per_leg?: number[];
    leg_descriptions?: ILegDescription[];
    flight_numbers: string[];
  }
  | null
  | undefined;


export interface AirlineLowestPrice {
  airline_code: string;
  airline_logo?: string;
  airline_name: string;
  price: number;
}

interface TimeFrame {
  departure: Arrival[];
  arrival: Arrival[];
}

interface Arrival {
  code: string;
  times: string;
}

